import { FieldValues, UseFormReturn } from 'react-hook-form';
import {
  type ShelfPatternEnum,
  type ShelfProductPatternEnum,
  type ShelfStyleEnum,
  GcfPriceLabelShapeTypeEnum,
  GcfPriceLabelTypeEnum,
  IComponent,
  ShelfPositionEnum,
  TextAlignmentEnum,
} from 'src/interfaces';
import { GcfMockData } from 'src/utils/gcf-helper';

export interface ISize {
  height: number;
  width: number;
}

export interface FormSelectorProps {
  hasTriedSaving?: boolean;
  saveToken?: object;
  showBackgroundError?: boolean;
  selectedComponent: IComponent;
  saveComponentConfig: (
    data: CommonFormProps['componentDetails']['config'],
  ) => void;
  setConfig: (data: CommonFormProps['componentDetails']['config']) => void;
  setSize: React.Dispatch<React.SetStateAction<ISize | undefined>>;
  setOrderType: React.Dispatch<React.SetStateAction<GcfMockData>>;
  form: UseFormReturn<any>;
}

export interface CommonFormProps<T extends FieldValues = any> {
  onSave: (data: CommonFormProps['componentDetails']['config']) => void;
  onChange: (
    data: CommonFormProps['componentDetails']['config'],
    path: string | string[],
    value: any | any[],
    keysToRemove?: Array<string>,
  ) => void;
  form?: UseFormReturn<T>;
  hasTriedSaving?: boolean;
  showBackgroundError?: boolean;
  componentDetails: IComponent<T>;
}

export enum GcfPriceDisplayFormat { // will be only used in Component Level as it doesnt have NONE
  INLINE = 'INLINE',
  MULTILINE = 'MULTILINE',
}

export enum HeaderLayout {
  TOP_BOTTOM = 'TOP_BOTTOM',
  LEFT_RIGHT = 'LEFT_RIGHT',
}

interface GcfTextBase {
  text: string;
  color?: string;
  font?: string;
  fontSize?: string;
  lineHeight?: string;
  height?: number;
  visible?: boolean;
  alignment?: TextAlignmentEnum;
  marginBottom?: string;
  width?: number;
  letterSpacing?: string;
  marginTop?: string;
  marginLeft?: string;
}

export interface GcfHeaderInterface extends GcfTextBase {
  overlay?: boolean;
  description?: GcfTextBase;
  layout?: HeaderLayout;
  known?: string;
  unknown?: string;
}

export interface IFlexDyHeader {
  header: GcfHeaderInterface;
}

interface GcfTitleInterface {
  visible?: boolean;
  text: string;
  color?: string;
  overlay?: boolean;
  font?: string;
  fontSize?: string;
  lineHeight?: string;
  height?: number;
}

interface GcfDisclaimerInterface {
  text: string;
  color?: string;
  visible?: boolean;
  position?: 'BOTTOM_HORIZONTAL' | 'RIGHT_VERTICAL';
  font?: string;
  fontSize?: string;
  lineHeight?: string;
}

export interface ScaleConfigBase {
  color: string;
  font: string;
  fontSize: string;
  lineHeight: string;
  flexWrap: string;
}

interface ScaleConfigWithHeight extends ScaleConfigBase {
  height: number | undefined;
}

interface DisclaimerScaleConfigBase extends ScaleConfigBase {
  backgroundColor: string;
}

export enum DISCLAIMER_POSITION {
  BOTTOM_HORIZONTAL = 'BOTTOM_HORIZONTAL',
  RIGHT_VERTICAL = 'RIGHT_VERTICAL',
}

export interface SubproductScaleType {
  label: ScaleConfigLabel;
  price: ScaleConfigBase;
  nutrition: ScaleConfigBase;
  flavor: ScaleConfigBase;
  disclaimer: ScaleConfigBase;
}

interface GcfPillShapePriceLabelInterface {
  backgroundColor?: string;
  border?: string;
}

interface ScaleConfigLabel
  extends ScaleConfigBase,
    GcfPillShapePriceLabelInterface {
  shape?: GcfPriceLabelShapeTypeEnum;
}

export interface ScaleType {
  padding: number;
  margin: number;
  title: ScaleConfigWithHeight;
  categoryTitle: ScaleConfigWithHeight;
  header: ScaleConfigWithHeight;
  headerDescription: ScaleConfigWithHeight;
  subheader: ScaleConfigWithHeight;
  subheaderDescription: ScaleConfigWithHeight;
  disclaimer: ScaleConfigBase | DisclaimerScaleConfigBase;
  disclaimerComponent: ScaleConfigBase | DisclaimerScaleConfigBase;
  products: ScaleConfigBase;
  subproducts: SubproductScaleType;
}

const GcfCommonComponentTitleConfigSchema = {
  type: 'object',
  properties: {
    visible: {
      type: 'boolean',
    },
    overlay: {
      type: 'boolean',
    },
    text: {
      type: 'string',
    },
    color: {
      type: 'string',
    },
    font: {
      type: 'string',
    },
    fontSize: {
      type: 'string',
    },
    lineHeight: {
      type: 'string',
    },
    height: {
      type: 'number',
    },
  },
  required: ['text'],
};

const FontConfigSchema = {
  properties: {
    font: {
      type: 'string',
    },
    fontSize: {
      type: 'string',
    },
    color: {
      type: 'string',
    },
    lineHeight: {
      type: 'string',
    },
  },
};

export const SubHeaderConfigSchema = {
  type: 'object',
  properties: {
    ...FontConfigSchema.properties,
    text: {
      type: 'string',
    },
    visible: {
      type: 'boolean',
    },
    height: {
      type: 'number',
    },
    marginBottom: {
      type: 'string',
    },
    border: {
      type: 'string',
    },
    borderRadius: {
      type: 'string',
    },
    backgroundColor: {
      type: 'string',
    },
    description: {
      type: 'object',
      properties: {
        ...FontConfigSchema.properties,
        text: {
          type: 'string',
        },
        visible: {
          type: 'boolean',
        },
        height: {
          type: 'number',
        },
        marginBottom: {
          type: 'string',
        },
      },
      required: ['text'],
    },
  },
  required: ['text'],
};

const GcfTextBaseSchema = {
  properties: {
    text: {
      type: 'string',
    },
    color: {
      type: 'string',
    },
    font: {
      type: 'string',
    },
    fontSize: {
      type: 'string',
    },
    lineHeight: {
      type: 'string',
    },
    marginBottom: {
      type: 'string',
    },
    height: {
      type: 'number',
    },
    visible: {
      type: 'boolean',
    },
    alignment: {
      type: 'string',
      enum: [
        TextAlignmentEnum.CENTER,
        TextAlignmentEnum.LEFT,
        TextAlignmentEnum.RIGHT,
      ],
    },
    width: {
      type: 'number',
    },
    letterSpacing: {
      type: 'string',
    },
    marginTop: {
      type: 'string',
    },
    marginLeft: {
      type: 'string',
    },
  },
  required: ['text'],
  additionalProperties: false,
};

export const GcfHeaderSchema = {
  properties: {
    ...GcfTextBaseSchema.properties,
    overlay: {
      type: 'boolean',
    },
    description: {
      type: 'object',
      properties: {
        ...GcfTextBaseSchema.properties,
      },
      required: ['text'],
      additionalProperties: false,
    },
    layout: {
      type: 'string',
      enum: [HeaderLayout.LEFT_RIGHT, HeaderLayout.TOP_BOTTOM],
    },
  },
  required: GcfTextBaseSchema.required,
  type: 'object',
  additionalProperties: false,
};

export const GcfCommonComponentConfigSchema = {
  properties: {
    autoScale: {
      type: 'object',
      properties: {
        isShared: {
          type: 'boolean',
        },
      },
      required: ['isShared'],
    },
    typeface: {
      type: 'object',
      properties: {
        regular: {
          type: 'string',
        },
        bold: {
          type: 'string',
        },
      },
      required: ['regular', 'bold'],
    },
    title: {
      anyOf: [
        GcfCommonComponentTitleConfigSchema,
        {
          type: 'array',
          items: { allOf: [GcfCommonComponentTitleConfigSchema] },
        },
      ],
    },
    header: GcfHeaderSchema,
    subheader: {
      type: 'array',
      items: { allOf: [SubHeaderConfigSchema] },
    },
    productStyle: {
      type: 'object',
      properties: {
        color: {
          type: 'string',
        },
      },
      required: [],
    },
    priceLabel: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: [
            GcfPriceLabelTypeEnum.Size,
            GcfPriceLabelTypeEnum.Acronym,
            GcfPriceLabelTypeEnum.None,
          ],
        },
        shape: {
          type: 'string',
          enum: ['NONE', 'PILL'],
        },
      },
      required: ['type', 'shape'],
    },
    subProductsDisplayFormat: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['INLINE', 'MULTILINE'],
        },
      },
      required: ['type'],
    },
    background: {
      type: 'string',
      minLength: 1,
    },
    datasource: {
      oneOf: [
        {
          type: 'string',
        },
      ],
    },
    /** This is not required, it is used for creative center to provide line up for live changes in the component */
    lineup: {
      type: 'array',
      items: {
        type: 'object',
      },
    },
    productList: {},
    disclaimer: {
      type: 'object',
      properties: {
        text: {
          type: 'string',
        },
        color: {
          type: 'string',
        },
        visible: {
          type: 'boolean',
        },
        position: {
          type: 'string',
          enum: ['BOTTOM_HORIZONTAL', 'RIGHT_VERTICAL'],
        },
        font: {
          type: 'string',
        },
        fontSize: {
          type: 'string',
        },
        lineHeight: {
          type: 'string',
        },
      },
      required: ['text'],
    },
    highlight: {
      type: 'object',
      properties: {
        color: { type: 'string' },
      },
    },
    cycling: {
      type: 'object',
      properties: {
        offset: {
          type: 'number',
        },
        order: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              mappingKeys: {
                type: 'array',
                items: {
                  type: 'string',
                },
              },
              duration: {
                type: 'number',
              },
            },
            required: ['mappingKeys', 'duration'],
          },
        },
      },
      required: ['order'],
    },
    scaleTypeKey: {
      type: 'string',
    },
    flavor: {
      type: 'object',
      properties: {
        visible: {
          type: 'boolean',
          default: false,
        },
      },
      required: ['visible'],
    },
    showComponentProductDisclaimer: {
      type: 'boolean',
      default: false,
    },
    show_nutrition: {
      type: 'boolean',
    },
    isFlavourCycling: {
      type: 'boolean',
      default: false,
    },
  },
  required: [],
};

interface FontBaseConfig {
  font?: string;
  fontSize?: string;
  color?: string;
  lineHeight?: string;
}

export interface SubHeaderDescriptionConfig extends FontBaseConfig {
  text: string;
  height?: number;
  visible?: boolean;
  marginBottom?: string;
}
export interface SubHeaderConfig extends FontBaseConfig {
  text: string;
  height?: number;
  visible?: boolean;
  marginBottom?: string;
  border?: string;
  borderRadius?: string;
  backgroundColor?: string;
  description: SubHeaderDescriptionConfig;
  marginLeft?: string;
}

export interface ComponentLineupStylesOverride
  extends Omit<SubproductScaleType, 'flavor' | 'disclaimer'> {
  flavor?: ScaleConfigBase;
  disclaimer?: ScaleConfigBase;
}

interface SbElementsHighlightInterface {
  color?: string;
  // shape: enum; // we may need to have a configurable shape in the future
}

interface SbTimelineCyclingOrderConfigInterface {
  mappingKeys: string[];
  duration: number; // milliseconds
}

interface SbTimelineCyclingConfigInterface {
  order: SbTimelineCyclingOrderConfigInterface[];
  offset: number; // milliseconds
}

interface GcfFlavorInterface {
  visible: boolean;
}

export interface GcfCommonComponentConfigInterface {
  autoScale?: {
    // ALL except hero-single-list + no-visual-single-list
    isShared: boolean;
  };
  /** @deprecated since R14*/
  typeface?: {
    // ALL
    regular: string;
    bold: string;
  };
  /** @deprecated since R14*/
  title?: GcfTitleInterface | GcfTitleInterface[]; // ALL except two category list components
  header?: GcfHeaderInterface;
  /** @deprecated since R14*/
  subheader?: SubHeaderConfig[];
  /** @deprecated since R14*/
  productStyle: {
    // ALL - Define the font color of all product related text
    color: string;
  };
  /** @deprecated since R14*/
  priceLabel: {
    type: GcfPriceLabelTypeEnum;
    shape: GcfPriceLabelShapeTypeEnum;
  };
  /** @deprecated since R14*/
  subProductsDisplayFormat?: {
    // ALL - these will be enums
    type: GcfPriceDisplayFormat; //Added this as subProductsDisplayFormat doesnt contains NONE
  };
  background: string; // ALL
  disclaimer?: GcfDisclaimerInterface; // ALL
  /** @deprecated since R14*/
  scaleTypeKey: string; // ALL
  /** Lineup is not required. It is used in creative center for live update of the component */
  highlight?: SbElementsHighlightInterface;
  cycling?: SbTimelineCyclingConfigInterface;
  /**
   * @deprecated since R21
   */
  flavor?: GcfFlavorInterface;
  showComponentProductDisclaimer?: boolean;
  /** @deprecated since R14*/
  show_nutrition?: boolean;
  isFlavourCycling: boolean;
}

enum ContinuousShelfStyleEnum {
  PERSPECTIVE = 'PERSPECTIVE',
  PROPORTIONAL = 'PROPORTIONAL',
}

export enum ShelfProductTextAlignmentEnum {
  LEFT = 'LEFT',
  CENTER = 'CENTER',
  RIGHT = 'RIGHT',
}

interface GcfLineupStyleInterface {
  type: ShelfStyleEnum | ContinuousShelfStyleEnum;
  pattern?: ShelfPatternEnum;
}

export interface GcfShelfStyleInterface extends GcfLineupStyleInterface {
  type: ShelfStyleEnum;
  pattern?: ShelfPatternEnum;
  order?: ShelfProductPatternEnum;
  textAlignment?: ShelfProductTextAlignmentEnum;
  padding?: number;
  position?: ShelfPositionEnum;
}
